/**
 * CaiT Academy landing page — Phase 0 marketing shell.
 *
 * Visual language matches clinicianassist.ai: Fraunces display heading,
 * Inter body, signature primary blue, soft healthcare shadows + warm
 * white surface. CaiT Academy is presented as an extension of the
 * parent site rather than a separate product.
 */
import type * as React from "react";

export function Home(): React.JSX.Element {
  return (
    <>
      {/* Hero */}
      <section className="bg-hero-gradient">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 lg:py-32">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Foundations Certificate · Phase 0
          </p>
          <h1 className="mt-4 max-w-3xl text-balance text-5xl font-semibold tracking-tight text-foreground sm:text-6xl lg:text-7xl">
            Cognitive AI Therapy,{" "}
            <span className="text-primary">credentialed.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
            The credentialing-grade curriculum for licensed clinicians who
            want to use conversational AI safely, ethically, and effectively
            in mental-health practice. Founding Cohort Registration Opens
            July 1st 2026. Apply NOW!
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="https://clinicianassist.ai/cait"
              className="inline-flex items-center justify-center rounded-lg bg-ca-gradient px-8 py-4 text-base font-semibold text-white shadow-card transition-all hover:-translate-y-px hover:shadow-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ca-indigo focus-visible:ring-offset-2 sm:text-lg"
            >
              Sign up NOW →
            </a>
            <a
              href="https://clinicianassist.ai"
              className="inline-flex items-center justify-center rounded-md border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:border-ca-purple/40 hover:bg-ca-gradient-subtle"
            >
              About Clinician Assist
            </a>
          </div>
        </div>
      </section>

      {/* Value triad */}
      <section className="border-y border-border bg-background">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-16 sm:grid-cols-3 sm:px-6 sm:py-20">
          <FeatureCard
            eyebrow="Curriculum"
            title="50 micro-lessons, 10 modules"
            body="Four-minute lessons with HeyGen video + article + a 5-item knowledge check. Built on the CaiT v3.0 framework."
          />
          <FeatureCard
            eyebrow="Assessment"
            title="50-item proctored exam"
            body="Stratified sampling across learning objectives + Bloom levels. Cycle of 3 attempts → 24-hour cooldown → repeat. No bake-offs."
          />
          <FeatureCard
            eyebrow="Credential"
            title="Capstone + signed PDF cert"
            body="1,500-word case essay graded against the published rubric. Cert is QR-verifiable at verify.cait.academy and valid for 3 years."
          />
        </div>
      </section>

      {/* Phase note */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="rounded-2xl border border-border bg-muted/40 p-8 shadow-soft sm:p-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Phase 0 — Founding Cohort
          </p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            10 hand-picked clinicians. One credential. Twelve weeks.
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
            The Founding Cohort is invitation-only. The curriculum, exam
            engine, capstone grader (Claude Sonnet 4.6 against a published
            rubric, human-reviewed on borderline scores), certificate PDF +
            QR verification, and recertification reminders are all live in
            production. Invitations open after pilot pre-flight clears.
          </p>
          <p className="mt-6 text-xs text-muted-foreground">
            CaiT Foundations is an education-only credential — not
            authorization for independent clinical practice or production
            Casey use (D18).
          </p>
        </div>
      </section>
    </>
  );
}

interface FeatureCardProps {
  eyebrow: string;
  title: string;
  body: string;
}

function FeatureCard({
  eyebrow,
  title,
  body,
}: FeatureCardProps): React.JSX.Element {
  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-soft transition hover:shadow-card sm:p-8">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
        {eyebrow}
      </p>
      <h3 className="mt-3 text-xl font-semibold tracking-tight text-card-foreground sm:text-2xl">
        {title}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        {body}
      </p>
    </div>
  );
}
